import * as React from 'react'
import { View, FlatList, Text, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import TextInputComponent from '@components/textInput/TextInput'
import SortSelector from '@components/modal/SortSelector'
import { RadioOption } from '@components/radio/Radio'

import AdminManifestationCard from '@components/card/AdminManifestationCard'

import { filterManifestations } from '@utils/admin'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import useReducer from '@store/useReducer'
import * as AdminStore from '@store/admin/admin'

import api from '@api/api'
import { isAfter } from 'date-fns'
import Logger from '@utils/logger'

const AdminOrgaListScreen = () => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const isPhone = useBreakpoint()[1] === 'phone'

  const manifestations = useReducer(AdminStore.store, (s) => s.manifestations)

  const [status, setStatus] = React.useState<'fetched' | 'loading'>('loading')
  const [search, setSearch] = React.useState('')

  const sorts: RadioOption[] = [
    { label: i18n.t('screens.admin.organizers.sorts.name'), value: 'name' },
    { label: i18n.t('screens.admin.organizers.sorts.dateDebutMontage'), value: 'dateDebutMontage' },
  ]

  const [reverse, setReverse] = React.useState(false)
  const [sort, setSort] = React.useState<RadioOption | undefined>(sorts[0])

  React.useEffect(() => {
    api.manifestation
      .all()
      .then(AdminStore.actions.setManifestations)
      .catch(Logger.error)
      .finally(() => setStatus('fetched'))
  }, [])

  const displayedData = React.useMemo(() => {
    const filtered = filterManifestations(manifestations, search)

    const sorted = sort
      ? filtered.sort(
          (a, b) =>
            (sort.value === 'name'
              ? a.libelle.localeCompare(b.libelle)
              : isAfter(new Date(a.dateDebutMontage), new Date(b.dateDebutMontage))
              ? 1
              : -1) * (reverse ? -1 : 1)
        )
      : filtered

    return sorted
  }, [manifestations, sort, reverse, search])

  const renderManifCard = ({ item }: { item: ManifestationResponse }) => (
    <ManifContainer activeOpacity={0.9} onPress={() => navigation.push(`/admin/organizers/${item.id}`)}>
      <AdminManifestationCard manifestation={item} />
    </ManifContainer>
  )

  const renderSearch = () => (
    <SearchContainer>
      <TextInputComponent
        onChange={setSearch}
        searchStyle="onDark"
        placeholder={i18n.t('screens.incidentList.search')}
        left={{ name: 'search' }}
        value={search}
      />
    </SearchContainer>
  )

  return (
    <Root
      loading={status === 'loading'}
      header={isPhone ? i18n.t('screens.admin.organizers.title') : undefined}
      renderSubHeader={isPhone ? renderSearch : undefined}
      fixed={isPhone}
      onLeft={navigation.goBack}>
      {!isPhone && (
        <HeaderContainer>
          <Header title={i18n.t('screens.admin.organizers.title')} renderBottom={renderSearch} />
        </HeaderContainer>
      )}
      <SortContainer>
        <SortSelector
          onOrderChange={() => setReverse(!reverse)}
          onSortChange={setSort}
          reverse={reverse}
          sorts={sorts}
          sort={sort}
        />
      </SortContainer>
      <FlatList
        data={displayedData}
        renderItem={renderManifCard}
        contentContainerStyle={{
          flexWrap: isPhone ? undefined : 'wrap',
          flexDirection: isPhone ? 'column' : 'row',
          marginVertical: 20,
          marginHorizontal: isPhone ? 16 : 25,
          justifyContent: isPhone ? 'center' : 'flex-start',
        }}
        ListEmptyComponent={<MissingInfo>{i18n.t('screens.admin.users.empty')}</MissingInfo>}
        showsVerticalScrollIndicator={false}
      />
    </Root>
  )
}

export default AdminOrgaListScreen

const HeaderContainer = styled(View)`
  z-index: 10;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const ManifContainer = styled(TouchableOpacity)`
  margin-bottom: 10px;
  ${(props) => (props.theme.breakpoint === 'phone' ? `margin: 4px;` : `margin-right: 16px;`)};
`

const SortContainer = styled(View)`
  padding: ${(props) => (props.theme.breakpoint !== 'phone' ? `16px 0 0 24px` : `10px 16px`)};
`

const SearchContainer = styled(View)`
  margin-top: ${(props) => (props.theme.breakpoint === 'phone' ? 12 : 24)}px;
`
